import { Outlet, NavLink, useLocation } from "react-router-dom";
import { prefetchRoute } from "@/lib/lazyPages";
import { ChevronLeft, ChevronRight, Menu, X, LogOut, Clock, MessageSquare, Settings, LifeBuoy, Eye } from "lucide-react";
import { useState, useEffect } from "react";
import { useAuth } from "@/lib/auth";
import { useBranding } from "@/lib/branding";
import { api } from "@/lib/api";
import { ChatPanel } from "@/components/ChatPanel";
import { ActionBell, ActionBellHost } from "@/components/shared/ActionBell";
import ReportButton from "@/components/ReportButton";
import { toast } from "sonner";
import { useCacheEvents } from "@/hooks/useCacheEvents";
import { NAV, buildGroups, DEFAULT_MOBILE_BAR } from "@/lib/navRegistry";
import { useNavLayout } from "@/hooks/useNavLayout";
import { SandboxBanner } from "@/components/shared/SandboxBanner";
import { Pin, PinOff } from "lucide-react";

// Floating widgets shrink to a dot after 3 s idle / while scrolling; hover expands them (systemwide)
const useIdleHide = (ms = 3000) => {
  const [idle, setIdle] = useState(false);
  useEffect(() => {
    let t = setTimeout(() => setIdle(true), ms);
    const wake = () => { setIdle(false); clearTimeout(t); t = setTimeout(() => setIdle(true), ms); };
    const scroll = () => { setIdle(true); clearTimeout(t); t = setTimeout(() => setIdle(true), ms); };
    window.addEventListener("mousemove", wake, { passive: true });
    window.addEventListener("keydown", wake);
    window.addEventListener("scroll", scroll, { passive: true, capture: true });
    return () => { clearTimeout(t); window.removeEventListener("mousemove", wake); window.removeEventListener("keydown", wake); window.removeEventListener("scroll", scroll, { capture: true }); };
  }, [ms]);
  return idle;
};

const DataAsOfStamp = () => {
  const [ts, setTs] = useState(null);
  const idle = useIdleHide();
  const loc = useLocation();
  useEffect(() => {
    const h = (e) => setTs(e.detail.ts);
    window.addEventListener("data-as-of", h);
    return () => window.removeEventListener("data-as-of", h);
  }, []);
  useEffect(() => { setTs(null); }, [loc.pathname]);
  if (!ts) return null;
  const d = new Date(ts);
  const stamp = d.toLocaleString("en-IN", { timeZone: "Asia/Kolkata", day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false });
  return (
    <div className={`group fixed bottom-4 left-4 md:left-[calc(var(--sidebar-w,200px)+16px)] z-[55] flex items-center gap-1.5 bg-white/90 backdrop-blur border border-[#E2E5EA] text-[#6B7280] text-[10px] font-semibold rounded-sm shadow-sm overflow-hidden ${idle ? "px-1 py-1 opacity-60 hover:opacity-100 hover:px-2.5 hover:py-1.5" : "px-2.5 py-1.5"}`}
      style={{ transition: "opacity 0.25s, padding 0.2s" }}
      title="Timestamp of the Odoo data currently displayed — click Refresh on the page for live numbers"
      data-testid="data-as-of-stamp" data-idle={idle ? "1" : "0"}>
      <Clock size={10} className="text-[#1B2A4A] shrink-0" />
      <span className={`whitespace-nowrap ${idle ? "hidden group-hover:inline" : ""}`}>Data as of {stamp} IST</span>
    </div>
  );
};

// Own component: idle state must not re-render AppLayout (that remounted the sidebar and reset its scroll)
const ChatFab = ({ onOpen }) => {
  const idle = useIdleHide();
  return (
    <button onClick={onOpen}
      className={`fixed bottom-20 md:bottom-5 right-5 z-50 bg-[#E32C17] hover:bg-[#C41E10] text-white rounded-full shadow-lg flex items-center justify-center ${idle ? "w-3 h-3 opacity-50 hover:w-12 hover:h-12 hover:opacity-100" : "w-12 h-12"}`}
      style={{ transition: "width 0.2s, height 0.2s, opacity 0.25s, background-color 0.15s" }} data-testid="chat-fab" data-idle={idle ? "1" : "0"} title="AI chat">
      <MessageSquare size={22} className={idle ? "hidden" : ""} />
    </button>
  );
};

const BgRefreshBadge = () => {
  const [active, setActive] = useState(false);
  useEffect(() => {
    const h = (e) => setActive(e.detail.active);
    window.addEventListener("bg-refresh", h);
    return () => window.removeEventListener("bg-refresh", h);
  }, []);
  if (!active) return null;
  return (
    <div className="fixed bottom-4 right-4 z-[60] flex items-center gap-2 bg-[#1B2A4A] text-white text-xs font-semibold px-3 py-2 rounded-sm shadow-lg" data-testid="bg-refresh-badge">
      <span className="inline-block w-3 h-3 border-2 border-white/40 border-t-white rounded-full animate-spin" />
      Updating data in background… app stays usable
    </div>
  );
};


export function AppLayout() {
  const _loc = useLocation();
  const { user: _u } = useAuth();
  useEffect(() => {
    if (_u?.username && _loc.pathname !== "/" && _loc.pathname !== "/login") {
      localStorage.setItem(`stancor_last_route:${_u.username}`, _loc.pathname + _loc.search);
    }
  }, [_loc.pathname, _loc.search, _u?.username]);
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [closedGroups, setClosedGroups] = useState(() => {
    try { return JSON.parse(localStorage.getItem("nav_closed_groups") || "{}"); } catch { return {}; }
  });
  const toggleGroup = (g) => setClosedGroups((prev) => {
    const next = { ...prev, [g]: !prev[g] };
    try { localStorage.setItem("nav_closed_groups", JSON.stringify(next)); } catch {}
    return next;
  });
  const location = useLocation();
  const { user, logout, showTimeout, dismissTimeout, timeRemaining, token, pageAllowed, preview, exitPreview, permsLoaded, role } = useAuth();
  const { logo, font } = useBranding();
  const { layout: navLayout, pins, togglePin } = useNavLayout(token);

  // Live cache-invalidation toast feed
  useCacheEvents(token);

  // Budget alerts — toast once per day per browser when a category crosses budget
  useEffect(() => {
    if (!token) return;
    const key = `budget_alert_${new Date().toISOString().slice(0, 10)}`;
    if (sessionStorage.getItem(key)) return;
    api.getBudgetAlerts().then((r) => {
      (r.data.alerts || []).forEach((a) => {
        toast.error(`Budget exceeded: ${a.category} billed ₹${(a.billed_mtd / 1e5).toFixed(1)} L against ₹${(a.budget / 1e5).toFixed(1)} L budget (+₹${(a.over_by / 1e5).toFixed(1)} L)`, { duration: 8000 });
      });
      sessionStorage.setItem(key, "1");
    }).catch(() => {});
  }, [token]);

  // Close mobile menu on navigation
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  // Close on resize to desktop
  useEffect(() => {
    const onResize = () => { if (window.innerWidth >= 768) setMobileOpen(false); };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // (Navigation Visibility card removed — Role Access is the single source of truth)

  // Nav visibility rules (in this order):
  //   1. Preview mode overrides everything for the admin (so they see exactly
  //      what the previewed role would see). Settings tab is force-shown so
  //      the admin can always exit the preview.
  //   2. Admin always sees everything — they need it to administer.
  //   3. Non-admin: RBAC decides visibility (pageAllowed based on role).
  //      RBAC is the single source of truth so admin overrides always take effect.
  const isAdminUser = user?.is_admin;
  const inPreview = !!preview && isAdminUser;
  // Until permissions arrive for a non-admin, show NOTHING extra — prevents the
  // "all tabs flash then disappear" on load. Admins render instantly.
  const permsPending = !permsLoaded && !isAdminUser && !inPreview;
  const canSee = (item) => !permsPending && (inPreview ? pageAllowed(item.permKey || item.to) : isAdminUser || pageAllowed(item.permKey || item.to));
  const navGroups = buildGroups(navLayout, preview?.role || role).map((g) => ({ ...g, items: g.items.filter(canSee) })).filter((g) => g.items.length);
  const pinnedItems = pins.map((k) => navGroups.flatMap((g) => g.items).find((i) => i.to === k)).filter(Boolean);
  const renderGroups = pinnedItems.length ? [{ id: "pinned", label: "Pinned", items: pinnedItems, pinned: true }, ...navGroups] : navGroups;
  const mobileBar = (navLayout?.mobile_bar?.length ? navLayout.mobile_bar : DEFAULT_MOBILE_BAR).map((k) => NAV.find((n) => n.to === k)).filter(Boolean);

  const SidebarContent = ({ isMobile }) => (
    <>
      {/* Logo */}
      <div className="flex items-center justify-between px-3 py-4 border-b border-[#1E293B]">
        <div className="flex items-center gap-2.5">
          <img src={logo || "/stancor-icon-48x48.png"} alt="Stancor" className="w-8 h-8 rounded-sm shrink-0 object-contain bg-white/95 p-0.5" />
          {(!collapsed || isMobile) && (
            <div>
              <div className="text-sm font-bold tracking-tight uppercase text-white leading-none" style={{ fontFamily: "var(--brand-font, 'Barlow Condensed')" }}>STANCOR</div>
              <div className="text-[9px] text-[#94A3B8] tracking-widest uppercase">Tubulars</div>
            </div>
          )}
        </div>
        {isMobile && (
          <button onClick={() => setMobileOpen(false)} className="p-1.5 rounded-sm text-[#94A3B8] hover:bg-white/10">
            <X size={20} />
          </button>
        )}
      </div>

      {/* Nav — grouped by department area */}
      <nav className="flex-1 py-2 px-2 overflow-y-auto">
        {permsPending && (
          <div className="space-y-2 px-2 pt-2" data-testid="nav-skeleton">
            {[...Array(6)].map((_, i) => <div key={i} className="h-7 rounded-sm bg-white/10 animate-pulse" />)}
          </div>
        )}
        {renderGroups.map(({ id: gid, label: g, items, pinned }) => {
          const showHeader = !collapsed || isMobile;
          const isClosed = showHeader && !!closedGroups[gid];
          return (
            <div key={gid} className="mb-0.5">
              {showHeader && (
                <button onClick={() => toggleGroup(gid)}
                  className={`w-full flex items-center justify-between px-2.5 pt-2.5 pb-1 text-[9px] font-bold uppercase tracking-widest hover:text-[#94A3B8] ${pinned ? "text-[#E32C17]" : "text-[#64748B]"}`}
                  data-testid={`nav-group-${gid}`}>
                  <span className="flex items-center gap-1">{pinned && <Pin size={9} />}{g}</span>
                  <ChevronRight size={10} className="shrink-0" style={{ transform: isClosed ? "none" : "rotate(90deg)", transition: "transform 0.15s" }} />
                </button>
              )}
              {!isClosed && items.map((item) => {
                const [toPath, toQuery] = item.to.split("?");
                const here = location.pathname + location.search;
                const siblingQueryActive = NAV.some((o) => o !== item && o.to.includes("?") && o.to.split("?")[0] === toPath && here.startsWith(o.to));
                const isActive = toQuery ? here.startsWith(item.to)
                  : item.to === "/" ? location.pathname === "/" : location.pathname.startsWith(toPath) && !siblingQueryActive;
                const isPinned = pins.includes(item.to);
                return (
                  <div key={`${gid}|${item.to}`} className="group/nav relative">
                  <NavLink to={item.to}
                    onMouseEnter={() => prefetchRoute(toPath)} onTouchStart={() => prefetchRoute(toPath)}
                    className={() => `flex items-center gap-2.5 px-2.5 py-2 md:py-1.5 rounded-sm text-sm font-medium ${
                      isActive ? "text-white bg-white/10 shadow-[inset_2px_0_0_#E32C17]" : "text-[#94A3B8] hover:text-white hover:bg-white/5"
                    }`}
                    aria-current={isActive ? "page" : undefined}
                    style={{ transition: "background-color 0.15s, color 0.15s" }}
                    data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}>
                    <item.icon size={17} strokeWidth={1.5} className="shrink-0" />
                    {(!collapsed || isMobile) && <span className="whitespace-nowrap truncate pr-5">{item.label}</span>}
                  </NavLink>
                  {(!collapsed || isMobile) && (
                    <button onClick={(e) => { e.preventDefault(); togglePin(item.to); }} title={isPinned ? "Unpin" : "Pin to top"}
                      className={`absolute right-1.5 top-1/2 -translate-y-1/2 p-0.5 rounded-sm ${isPinned ? "text-[#E32C17]" : "text-[#64748B] opacity-0 group-hover/nav:opacity-100 hover:text-white"}`}
                      data-testid={`nav-pin-${item.to.replace(/[^a-z0-9]+/gi, "-")}`}>
                      {isPinned ? <PinOff size={11} /> : <Pin size={11} />}
                    </button>
                  )}
                  </div>
                );
              })}
            </div>
          );
        })}
      </nav>

      {/* User + Settings + Logout */}
      <div className="border-t border-[#1E293B] px-2 py-2 space-y-1">
        {(!collapsed || isMobile) && user && (
          <div className="px-2.5 py-1.5 text-[10px] text-[#64748B] truncate">{user.name || user.username}</div>
        )}

        {/* My Actions bell — everyone */}
        <ActionBell collapsed={collapsed && !isMobile} />

        {/* Help — everyone */}
        <NavLink to="/help"
          className={({ isActive }) =>
            `flex items-center gap-2.5 px-2.5 py-2 rounded-sm text-sm font-medium ${
              isActive ? "text-white bg-white/10 shadow-[inset_2px_0_0_#E32C17]" : "text-[#94A3B8] hover:text-white hover:bg-white/5"
            }`
          }
          data-testid="nav-help">
          <LifeBuoy size={16} strokeWidth={1.5} className="shrink-0" />
          {(!collapsed || isMobile) && <span>Help</span>}
        </NavLink>

        {/* Settings — admin only */}
        {user?.is_admin && (
          <NavLink to="/settings"
            className={({ isActive }) =>
              `flex items-center gap-2.5 px-2.5 py-2 rounded-sm text-sm font-medium ${
                isActive ? "text-white bg-white/10 shadow-[inset_2px_0_0_#E32C17]" : "text-[#94A3B8] hover:text-white hover:bg-white/5"
              }`
            }
            data-testid="nav-settings">
            <Settings size={16} strokeWidth={1.5} className="shrink-0" />
            {(!collapsed || isMobile) && <span>Settings</span>}
          </NavLink>
        )}

        <button onClick={logout}
          className="flex items-center gap-2.5 px-2.5 py-2 rounded-sm text-sm font-medium text-[#F87171] hover:bg-white/5 w-full"
          data-testid="logout-btn">
          <LogOut size={16} strokeWidth={1.5} className="shrink-0" />
          {(!collapsed || isMobile) && <span>Sign Out</span>}
        </button>
      </div>

      {/* Collapse (desktop only) */}
      {!isMobile && (
        <button onClick={() => setCollapsed(!collapsed)}
          className="mx-2 mb-3 p-1.5 rounded-sm text-[#64748B] hover:text-white hover:bg-white/10 flex items-center justify-center">
          {collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
        </button>
      )}
    </>
  );

  return (
    <div className="flex min-h-screen bg-[#F8F9FB]">
      <ActionBellHost />
      <ReportButton />
      <BgRefreshBadge />
      <DataAsOfStamp />
      {/* Desktop Sidebar */}
      <aside className="hidden md:flex sticky top-0 h-screen border-r border-[#1E293B] bg-[#0F172A] flex-col shrink-0 z-50"
        style={{ width: collapsed ? 60 : 200, transition: "width 0.2s ease" }} data-testid="sidebar">
        {SidebarContent({ isMobile: false })}
      </aside>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          <div className="absolute inset-0 bg-black/30" onClick={() => setMobileOpen(false)} />
          <aside className="absolute left-0 top-0 bottom-0 w-[240px] bg-[#0F172A] border-r border-[#1E293B] flex flex-col shadow-xl"
            data-testid="mobile-sidebar">
            {SidebarContent({ isMobile: true })}
          </aside>
        </div>
      )}

      {/* Main content */}
      <div className="flex-1 min-w-0 overflow-auto pb-16 md:pb-0">
        {/* Mobile top bar */}
        <div className="md:hidden sticky top-0 z-40 bg-white border-b border-[#E2E5EA] px-3 py-2.5 flex items-center gap-3">
          <button onClick={() => setMobileOpen(true)} className="p-1.5 rounded-sm text-[#6B7280] hover:bg-[#F1F3F5]"
            data-testid="mobile-menu-btn">
            <Menu size={22} />
          </button>
          <div className="flex items-center gap-2">
            <img src={logo || "/stancor-icon-32x32.png"} alt="Stancor" className="w-6 h-6 rounded-sm object-contain" />
            <span className="text-sm font-bold tracking-tight uppercase text-[#1A1D21]" style={{ fontFamily: "var(--brand-font, 'Barlow Condensed')" }}>STANCOR</span>
          </div>
        </div>
        {/* Session timeout warning */}
        {showTimeout && (
          <div className="bg-amber-50 border-b border-amber-200 px-4 py-2 flex items-center justify-between" data-testid="timeout-warning">
            <div className="flex items-center gap-2 text-sm text-amber-700">
              <Clock size={14} />
              <span>Session expires in <strong>{timeRemaining} min</strong></span>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={dismissTimeout} className="text-xs text-amber-600 hover:underline">Dismiss</button>
              <button onClick={logout} className="text-xs text-red-600 hover:underline">Sign Out</button>
            </div>
          </div>
        )}
        <SandboxBanner />
        {/* Role preview banner (admin only) — sticky so it always appears at top of any page */}
        {inPreview && (
          <div className="bg-violet-600 text-white px-4 py-2 flex items-center justify-between shadow-sm sticky top-0 z-30" data-testid="preview-banner">
            <div className="flex items-center gap-2 text-sm">
              <Eye size={14} />
              <span className="flex items-center flex-wrap gap-x-1">
                <span>Previewing as</span>
                <span className="relative group" data-testid="preview-role-hover">
                  <strong className="uppercase tracking-wider underline decoration-white/50 underline-offset-2 decoration-dotted cursor-help">
                    {preview.role}
                  </strong>
                  {/* Role reference card — appears on hover */}
                  <span
                    className="hidden group-hover:block absolute left-0 top-full mt-2 z-40 bg-white text-[#1A1D21] rounded-md shadow-xl border border-[#E2E5EA] w-64 p-3 normal-case"
                    data-testid="preview-role-card"
                  >
                    <div className="text-[10px] uppercase tracking-widest text-[#6B7280] mb-1">
                      Pages visible to <b className="text-[#7C3AED]">{preview.role}</b>
                    </div>
                    {preview.role === "admin" ? (
                      <div className="text-xs text-[#374151]">
                        <b>Full access</b> — every page including Settings.
                      </div>
                    ) : (preview.pages || []).length === 0 ? (
                      <div className="text-xs text-[#9CA3AF] italic">No pages allowed.</div>
                    ) : (
                      <ul className="space-y-1 max-h-64 overflow-y-auto">
                        {(preview.pages || []).map((p) => {
                          const item = NAV.find((n) => n.to === p);
                          const label = item ? item.label : (p === "/settings" ? "Settings" : p);
                          const Icon = item?.icon;
                          return (
                            <li key={p} className="flex items-center gap-2 text-xs text-[#1A1D21]">
                              {Icon ? <Icon size={12} className="text-[#7C3AED] shrink-0" /> : <span className="w-3 h-3 shrink-0" />}
                              <span>{label}</span>
                              <span className="ml-auto text-[10px] font-mono text-[#9CA3AF]">{p}</span>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                    <div className="mt-2 pt-2 border-t border-[#E2E5EA] text-[10px] text-[#6B7280]">
                      {(preview.pages || []).length} page{(preview.pages || []).length === 1 ? "" : "s"} · admin API access is unaffected
                    </div>
                    {/* Arrow */}
                    <span className="absolute -top-1 left-4 w-2 h-2 bg-white border-l border-t border-[#E2E5EA] rotate-45"></span>
                  </span>
                </span>
                {preview.username && <span className="opacity-80">· {preview.username}</span>}
                <span className="ml-2 text-white/70 text-xs hidden sm:inline">— sidebar reflects what this role sees</span>
              </span>
            </div>
            <button
              onClick={exitPreview}
              data-testid="preview-exit-btn"
              className="text-xs font-semibold bg-white/15 hover:bg-white/25 px-3 py-1 rounded-sm transition-colors"
            >
              Exit preview
            </button>
          </div>
        )}
        <div className="pb-14">
          <Outlet />
        </div>
      </div>

      {/* Floating chat button (hidden on /chat page) — shrinks to a dot when idle */}
      {location.pathname !== "/chat" && !chatOpen && <ChatFab onOpen={() => setChatOpen(true)} />}

      {/* Slide-out chat panel */}
      {chatOpen && location.pathname !== "/chat" && (
        <div className="fixed right-0 top-0 bottom-0 z-50 w-full sm:w-[380px] bg-white border-l border-[#E2E5EA] shadow-2xl">
          <ChatPanel isOpen={chatOpen} onClose={() => setChatOpen(false)} />
        </div>
      )}

      {/* Mobile bottom nav — one-thumb factory use */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 z-50 bg-[#0F172A] border-t border-[#1E293B] flex"
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }} data-testid="mobile-bottom-nav">
        {mobileBar.filter((i) => !permsPending && ((isAdminUser && !inPreview) || pageAllowed(i.permKey || i.to))).map((i) => (
          <NavLink key={i.to} to={i.to} onTouchStart={() => prefetchRoute(i.to)}
            className={({ isActive }) => `flex-1 flex flex-col items-center justify-center gap-0.5 py-2 min-h-[56px] ${
              isActive ? "text-white" : "text-[#94A3B8]"}`}
            data-testid={`bottom-nav-${i.label.toLowerCase()}`}>
            {({ isActive }) => (<>
              <span className={`h-0.5 w-8 rounded-full mb-0.5 ${isActive ? "bg-[#E32C17]" : "bg-transparent"}`} />
              <i.icon size={20} />
              <span className="text-[10px] font-bold truncate max-w-[64px]">{i.label.replace("Daily Production", "DPR").replace("Tasks & Work", "Tasks")}</span>
            </>)}
          </NavLink>
        ))}
        <button onClick={() => setMobileOpen(true)}
          className="flex-1 flex flex-col items-center justify-center gap-0.5 py-2 min-h-[56px] text-[#94A3B8]"
          data-testid="bottom-nav-menu">
          <span className="h-0.5 w-8 mb-0.5" />
          <Menu size={20} />
          <span className="text-[10px] font-bold">Menu</span>
        </button>
      </nav>
    </div>
  );
}

